import LS2Request from "@enact/webos/LS2Request";
import type { LGVoiceResponse } from "../types/lg-voice";
import type { AudioError } from "../types/audio";
import { Howl } from "howler";

declare global {
  interface Window {
    lgMicDebug?: {
      startRecording: () => void;
      stopRecording: () => void;
      getStatus: () => string;
      service: LGMicrophoneService;
    };
  }
}

export class LGMicrophoneService {
  private recordRequest: LS2Request | null = null;
  private voiceTicket: string | null = null;
  private websocketUrl: string | null = null;
  private websocket: WebSocket | null = null;
  private status: string = "idle";
  private audioContext: AudioContext | null = null;
  private destination: MediaStreamAudioDestinationNode | null = null;
  private activeHowls: Set<Howl> = new Set();

  constructor() {
    // Expose debug functions globally
    window.lgMicDebug = {
      startRecording: () => {
        console.info("🎤 Debug: Attempting to start recording...");
        this.startRecording(
          () => console.info("🎤 Debug: Recording started"),
          () => console.info("🎤 Debug: Recording stopped"),
          (error) => console.error("🎤 Debug: Error:", error),
        );
      },
      stopRecording: () => {
        console.info("🎤 Debug: Attempting to stop recording...");
        this.stopRecording();
      },
      getStatus: () => {
        console.debug("🎤 Debug: Current status:", this.status);
        return this.status;
      },
      service: this,
    };
  }

  async startRecording(
    onStart: () => void,
    onStop: () => void,
    onError: (error: AudioError) => void,
  ) {
    console.log("🎤 LGMicrophoneService: Attempting to start recording...");
    this.status = "starting";

    try {
      this.recordRequest = new LS2Request().send({
        service: "luna://com.webos.service.voiceconductor",
        method: "recordVoice",
        parameters: {
          subscribe: true,
          socketType: "websocket",
        },
        onSuccess: (res: LGVoiceResponse) => {
          console.log("🎤 LGMicrophoneService: Received event:", res);

          if (res.event === "recordingStart") {
            this.status = "recording";
            this.voiceTicket = res.voiceTicket ?? null;
            this.websocketUrl = res.websocket ?? null;

            if (this.websocketUrl) {
              this.initializeWebSocket(this.websocketUrl, onError);
            }

            console.log(
              "🎤 LGMicrophoneService: Recording started with ticket:",
              this.voiceTicket,
            );
            console.log(
              "🎤 LGMicrophoneService: Websocket URL:",
              this.websocketUrl,
            );
            onStart();
          } else if (res.event === "recordingStop") {
            this.status = "stopped";
            console.log("🎤 LGMicrophoneService: Recording stopped");
            this.cleanup();
            onStop();
          } else if (res.event === "error") {
            this.status = "error";
            console.error("🎤 LGMicrophoneService: Error event received:", {
              code: res.errorCode,
              text: res.errorText,
            });
            this.cleanup();
            onError({
              code: res.errorCode ?? "RECORDING_ERROR",
              message: res.errorText ?? "Unknown error occurred",
            });
          }
        },
        onFailure: (err: LGVoiceResponse) => {
          this.status = "error";
          console.error(
            "🎤 LGMicrophoneService: Failed to start recording:",
            err,
          );
          this.cleanup();
          onError({
            code: err.errorCode ?? "RECORDING_ERROR",
            message: err.errorText ?? "Failed to start recording",
            originalError: err,
          });
        },
      });
    } catch (err) {
      this.status = "error";
      this.cleanup();
      onError({
        code: "INITIALIZATION_ERROR",
        message: "Failed to initialize recording",
        originalError: err,
      });
    }
  }

  private initializeWebSocket(
    url: string,
    onError: (error: AudioError) => void,
  ) {
    try {
      this.websocket = new WebSocket(url);
      this.websocket.binaryType = "arraybuffer";

      this.websocket.onmessage = (event) => {
        if (event.data instanceof ArrayBuffer) {
          this.processAudioData(event.data);
        }
      };

      this.websocket.onerror = (error) => {
        console.error("🎤 LGMicrophoneService: WebSocket error:", error);
        onError({
          code: "WEBSOCKET_ERROR",
          message: "WebSocket connection error",
          originalError: error,
        });
      };

      this.websocket.onclose = () => {
        console.log("🎤 LGMicrophoneService: WebSocket connection closed");
      };
    } catch (error) {
      console.error(
        "🎤 LGMicrophoneService: Failed to initialize WebSocket:",
        error,
      );
      onError({
        code: "WEBSOCKET_INITIALIZATION_ERROR",
        message: "Failed to initialize WebSocket connection",
        originalError: error,
      });
    }
  }

  stopRecording() {
    if (this.voiceTicket) {
      console.log(
        "🎤 LGMicrophoneService: Stopping recording with ticket:",
        this.voiceTicket,
      );
      this.status = "stopping";

      new LS2Request().send({
        service: "luna://com.webos.service.voiceconductor",
        method: "stopRecordingVoice",
        parameters: {
          voiceTicket: this.voiceTicket,
        },
        onSuccess: (res: LGVoiceResponse) => {
          console.log(
            "🎤 LGMicrophoneService: Stop recording request successful:",
            res,
          );
          // Note: The actual stop event will come through the recordVoice subscription
        },
        onFailure: (err: LGVoiceResponse) => {
          console.error(
            "🎤 LGMicrophoneService: Failed to stop recording:",
            err,
          );
          // Even if the stop request fails, we should cleanup
          this.cleanup();
        },
      });
    } else {
      console.warn("🎤 LGMicrophoneService: No voice ticket to stop recording");
    }
  }

  async getAudioStream(): Promise<MediaStream> {
    try {
      // Create audio context if not exists
      if (!this.audioContext) {
        this.audioContext = new AudioContext({
          sampleRate: 16000,
          latencyHint: "interactive",
        });
      }

      // Create a MediaStreamDestination
      this.destination = this.audioContext.createMediaStreamDestination();

      return this.destination.stream;
    } catch (err) {
      throw {
        code: "INITIALIZATION_ERROR",
        message: "Failed to initialize audio context",
        originalError: err,
      } as AudioError;
    }
  }

  private processAudioData = (data: ArrayBuffer) => {
    if (!this.audioContext || !this.destination) return;

    try {
      // Convert the PCM data to a Blob that Howler can use
      const view = new DataView(data);
      const pcmData = new Int16Array(data.byteLength / 2);
      
      for (let i = 0; i < data.byteLength / 2; i++) {
        pcmData[i] = view.getInt16(i * 2, true);
      }
      
      // Convert PCM to WAV format (add WAV header)
      const wavBuffer = this.pcmToWav(pcmData, 1, 16000);
      const wavBlob = new Blob([wavBuffer], { type: 'audio/wav' });
      const wavUrl = URL.createObjectURL(wavBlob);
      
      console.debug('🎤 LGMicrophoneService: Created WAV blob, size:', wavBlob.size, 'bytes');
      
      // Create a new Howl instance for direct playback
      const soundHowl = new Howl({
        src: [wavUrl],
        format: ['wav'],
        html5: false, // Use Web Audio API instead of HTML5 Audio
        preload: true, // Preload to ensure smooth playback
        autoplay: false, // Manually control playback
        volume: 1.0,
        onload: () => {
          console.debug('🎤 LGMicrophoneService: Howl loaded successfully');
        },
        onloaderror: (id, error) => {
          console.error('🎤 LGMicrophoneService: Howl load error:', error);
          console.error('🎤 LGMicrophoneService: Error with URL:', wavUrl);
          URL.revokeObjectURL(wavUrl);
          this.activeHowls.delete(soundHowl);
        },
        onend: () => {
          URL.revokeObjectURL(wavUrl);
          this.activeHowls.delete(soundHowl);
        }
      });
      
      // Add to active set and play
      this.activeHowls.add(soundHowl);
      soundHowl.play();
      
      // Also feed the PCM to the MediaStreamDestination for compatibility
      // This direct approach bypasses any Howler issues
      const buffer = this.audioContext.createBuffer(
        1,
        data.byteLength / 2,
        16000,
      );
      const channelData = new Float32Array(data.byteLength / 2);
      
      // Convert 16-bit PCM to float32
      for (let i = 0; i < data.byteLength / 2; i++) {
        channelData[i] = view.getInt16(i * 2, true) / 32768;
      }
      
      buffer.getChannelData(0).set(channelData);
      
      // Play the audio through the MediaStream
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(this.destination);
      source.start();
    } catch (error) {
      console.error('🎤 LGMicrophoneService: Error processing audio data:', error);
    }
  };
  
  // Convert PCM to WAV format (add proper WAV headers)
  private pcmToWav(samples: Int16Array, numChannels: number, sampleRate: number): ArrayBuffer {
    const buffer = new ArrayBuffer(44 + samples.length * 2);
    const view = new DataView(buffer);
    
    // RIFF chunk descriptor
    this.writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + samples.length * 2, true);
    this.writeString(view, 8, 'WAVE');
    
    // "fmt " sub-chunk
    this.writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true); // subchunk1Size (16 for PCM)
    view.setUint16(20, 1, true); // audioFormat (1 for PCM)
    view.setUint16(22, numChannels, true); // numChannels
    view.setUint32(24, sampleRate, true); // sampleRate
    view.setUint32(28, sampleRate * numChannels * 2, true); // byteRate
    view.setUint16(32, numChannels * 2, true); // blockAlign
    view.setUint16(34, 16, true); // bitsPerSample
    
    // "data" sub-chunk
    this.writeString(view, 36, 'data');
    view.setUint32(40, samples.length * 2, true); // subchunk2Size
    
    // Write the PCM samples
    const offset = 44;
    for (let i = 0; i < samples.length; i++) {
      view.setInt16(offset + i * 2, samples[i], true);
    }
    
    return buffer;
  }
  
  private writeString(view: DataView, offset: number, string: string): void {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  cleanup() {
    // Clear the subscription
    if (this.recordRequest !== null) {
      this.recordRequest.cancel();
      this.recordRequest = null;
    }

    // Close the websocket
    if (this.websocket !== null) {
      if (this.websocket.readyState === WebSocket.OPEN) {
        this.websocket.close();
      }
      this.websocket = null;
    }
    
    // Clean up any active Howl instances
    this.activeHowls.forEach(howl => {
      howl.stop();
      howl.unload();
    });
    this.activeHowls.clear();
    
    // Reset variables
    this.voiceTicket = null;
    this.websocketUrl = null;
    this.status = "idle";
    
    // Close audio context if needed
    if (this.audioContext) {
      try {
        this.audioContext.close().catch(err => {
          console.warn('🎤 LGMicrophoneService: Error closing AudioContext:', err);
        });
      } catch (err) {
        console.warn('🎤 LGMicrophoneService: Error closing AudioContext:', err);
      }
      this.audioContext = null;
      this.destination = null;
    }
  }
}
